/**
 * TimeDrum — iOS-style momentum-scroll drum picker.
 *
 * Rolls 4:00 AM → 8:00 PM in 15-minute intervals (65 options), snap-to-center.
 * Selection band highlights the middle row; adjacent rows dim and shrink.
 * Emits `HH:MM` (24h) via onChange; renders "H:MM AM/PM" 12h.
 *
 * The drum wins its own gestures — it uses native scroll (touch-action:
 * pan-y) inside a fixed-height frame so the sheet's swipe gesture never
 * conflicts. On scroll-end (150ms after last scroll), snaps to the
 * nearest option.
 *
 * Reduced-motion: `scroll-behavior: auto`, no smooth-scroll on snap.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";

// Operating-hours config. Change these two constants when Link expands
// its hours — nothing else needs to change.
const START_HOUR = 4;   // 4:00 AM
const END_HOUR   = 20;  // 8:00 PM (inclusive)
const INTERVAL_MIN = 15;

const ROW_HEIGHT = 48;   // px — must match .drum-row CSS
const VISIBLE_ROWS = 5;  // odd number so the middle is the selection
const CENTER_INDEX = Math.floor(VISIBLE_ROWS / 2);

function buildOptions() {
  const opts = [];
  for (let h = START_HOUR; h <= END_HOUR; h++) {
    for (let m = 0; m < 60; m += INTERVAL_MIN) {
      if (h === END_HOUR && m > 0) break; // stop exactly at 8:00 PM
      opts.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return opts;
}

function to12h(hhmm) {
  if (!hhmm) return "—";
  // Legacy range strings ("HH:MM-HH:MM") — display the START only.
  const start = String(hhmm).split("-")[0];
  const [hStr, mStr] = start.split(":");
  const h = parseInt(hStr, 10);
  if (Number.isNaN(h)) return "—";
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${mStr} ${suffix}`;
}

/** Formatted "H:MM–H:MM AM/PM" 15-min window for a given HH:MM start.
    Also accepts legacy range strings ("HH:MM-HH:MM") emitted by the
    old WindowSelect — takes the first half to migrate cleanly. */
export function formatWindow(hhmm) {
  if (!hhmm) return "—";
  // Strip any legacy "-HH:MM" suffix so we always work with an HH:MM start.
  const start = String(hhmm).split("-")[0];
  const [hStr, mStr] = start.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return "—";
  const endM = m + INTERVAL_MIN;
  const endH = h + Math.floor(endM / 60);
  const endMm = endM % 60;
  const suffixEnd = endH >= 12 ? "PM" : "AM";
  const endH12 = ((endH + 11) % 12) + 1;
  return `${to12h(start).replace(/ (AM|PM)$/, "")}–${endH12}:${String(endMm).padStart(2, "0")} ${suffixEnd}`;
}

export default function TimeDrum({ value, onChange, testId = "time-drum" }) {
  const options = useMemo(buildOptions, []);
  const scrollerRef = useRef(null);
  const snapTimeoutRef = useRef(null);
  const [snapping, setSnapping] = useState(false);

  // Anchor the drum on the current value (or a sensible default).
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const idx = Math.max(0, options.indexOf(value));
    // Immediate positioning (no smooth) on first paint so the drum
    // never renders "at 4:00 AM" for a beat before jumping.
    el.scrollTop = idx * ROW_HEIGHT;
  }, []);

  // Re-anchor if the value changes externally (e.g. rider taps the
  // opposite chip and returns).
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const idx = Math.max(0, options.indexOf(value));
    const target = idx * ROW_HEIGHT;
    if (Math.abs(el.scrollTop - target) > 4) {
      el.scrollTo({ top: target, behavior: "smooth" });
    }
  }, [value, options]);

  const onScroll = () => {
    if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
    snapTimeoutRef.current = setTimeout(() => {
      const el = scrollerRef.current;
      if (!el) return;
      const rawIdx = el.scrollTop / ROW_HEIGHT;
      const idx = Math.max(0, Math.min(options.length - 1, Math.round(rawIdx)));
      const target = idx * ROW_HEIGHT;
      setSnapping(true);
      el.scrollTo({ top: target, behavior: "smooth" });
      const next = options[idx];
      if (next !== value) {
        try { navigator.vibrate?.(6); } catch (_) { /* noop */ }
        onChange?.(next);
      }
      setTimeout(() => setSnapping(false), 220);
    }, 140);
  };

  return (
    <div
      className="relative w-full select-none"
      style={{
        height: ROW_HEIGHT * VISIBLE_ROWS,
        fontFamily: "var(--font-display)",
      }}
      data-testid={testId}
    >
      {/* Selection band (center row) — sits behind the scroller so it
          doesn't intercept touches; visually highlights the middle. */}
      <div
        className="pointer-events-none absolute left-2 right-2 top-1/2 -translate-y-1/2 rounded-2xl bg-white/[0.08] border border-white/15"
        style={{ height: ROW_HEIGHT }}
        aria-hidden="true"
      />
      {/* Top + bottom fades so far rows dim into the sheet — pure CSS. */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-10"
        style={{
          height: ROW_HEIGHT * CENTER_INDEX,
          background: "linear-gradient(180deg, rgba(10,17,40,0.85) 0%, rgba(10,17,40,0) 100%)",
        }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-10"
        style={{
          height: ROW_HEIGHT * CENTER_INDEX,
          background: "linear-gradient(0deg, rgba(10,17,40,0.85) 0%, rgba(10,17,40,0) 100%)",
        }}
        aria-hidden="true"
      />
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="relative h-full overflow-y-auto no-scrollbar"
        style={{
          scrollSnapType: "y mandatory",
          touchAction: "pan-y",       // drum wins vertical scroll inside its bounds
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
        }}
        data-testid={`${testId}-scroller`}
      >
        {/* Top padding so the first option can center in the band. */}
        <div style={{ height: ROW_HEIGHT * CENTER_INDEX }} aria-hidden="true" />
        {options.map((opt) => {
          const isSelected = opt === value;
          return (
            <div
              key={opt}
              className="flex items-center justify-center text-white tabular-nums"
              style={{
                height: ROW_HEIGHT,
                scrollSnapAlign: "center",
                scrollSnapStop: "always",
                fontSize: isSelected ? 22 : 18,
                fontWeight: isSelected ? 800 : 600,
                opacity: isSelected ? 1 : 0.4,
                letterSpacing: "0.01em",
                transition: `opacity var(--motion-base) var(--ease-out), font-size var(--motion-base) var(--ease-out)`,
              }}
              data-testid={isSelected ? `${testId}-selected` : undefined}
              data-value={opt}
            >
              {to12h(opt)}
            </div>
          );
        })}
        {/* Bottom padding mirrors the top so the last option can center. */}
        <div style={{ height: ROW_HEIGHT * CENTER_INDEX }} aria-hidden="true" />
      </div>
      {/* Range hint at the bottom edge — cements the operating hours. */}
      <div
        className="pointer-events-none absolute inset-x-0 -bottom-6 text-center text-[10px] uppercase tracking-[0.22em] text-white/40"
        style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
        aria-hidden="true"
      >
        4:00 AM · 8:00 PM
      </div>
    </div>
  );
}
